import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Calendar, CalendarDays, Users, Play, Plus, Edit, Clock, MapPin, UserPlus, UserMinus, Copy, FileText, Download, MoreHorizontal, Trophy, Shield, ClipboardList, Paperclip, Globe2, Hash, AlertTriangle } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { useOrganization } from '../contexts/OrganizationContext';
import { clubCapturaEsteTorneo } from '../lib/torneoOrigen';
import { partirPorMundo, MUNDO_INTERNO, MUNDO_RED } from '../lib/mundoTorneo';
import { agruparPorTemporada, SIN_TEMPORADA } from '../lib/historicoTemporada';
import { ETAPA_LABELS, ETAPA_STYLES, etapaDeTorneo } from './torneos/torneoStatus';
import { puedeCapturarJuegos } from '../lib/permisosJuegos';
import { usePermissions } from '../hooks/usePermissions';
import { useConfirmDialog } from '../hooks/useConfirmDialog';
import { usePhotoUpload } from '../hooks/usePhotoUpload';
import { numeroEnGrupo, detectarNumeros } from '../lib/numerosGrupo';
import { cn } from '../lib/utils';
import NumerosGrupoEditor from './groups/NumerosGrupoEditor';
import { DetailLayout } from './layouts';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from './ui/dropdown-menu';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  Badge,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from './ui';
import PhotoAvatarButton from './ui/PhotoAvatarButton';
import InscribirGrupoModal from './torneos/InscribirGrupoModal';
import ElegirTorneoModal from './torneos/ElegirTorneoModal';
import GroupRosterModal from './groups/GroupRosterModal';
import GroupEditorModal from './groups/GroupEditorModal';
import EstadoParticipacionBadges from './groups/EstadoParticipacionBadges';
import {
  getGroupById,
  getGroupPlayers,
  getEstadoJugadoresGrupo,
  deleteGroup,
  getAllCoaches,
  getSeriesEntrenamiento,
  getCategorias,
  getInscripcionesByGrupoIds,
  getEstadisticasGrupoEnTorneo,
  getAsistenciaGrupoEnTorneo,
  assignStaffToGroup,
  updateStaffRole,
  removeStaffFromGroup,
  getTrainingProgramAgenda,
  getAttendanceStatsByGroup,
  supabase
} from '../lib/supabase';
import { formatLocalDate, parseLocalDate, getMexicoDateString } from '../lib/dateUtils';
import { DEFAULT_SPORT, normalizeSportValue } from '../constants/sports';
import { useUnsavedGuard } from '../hooks/useUnsavedGuard';

// El numero que el jugador lleva EN ESTE GRUPO, con sus dos problemas leidos en el mismo
// sitio: repetido en rojo, sin numero en tenue.
//
// Primero se marco el repetido solo con el borde, como el input del editor. No sirve: en
// el editor el campo es BLANCO y el borde rojo salta, pero aqui el chip va relleno de
// negro y el borde se pierde. El dueno lo vio en dos segundos ("casi no se distingue").
// Se pinta con el mismo peso que la insignia de "fuera de categoria", que es la que si
// se lee de un vistazo en esa fila.
const NumeroDelGrupo = ({ numero, repetido, tam = 'sm' }) => {
  const sinNumero = !numero;
  const titulo = sinNumero
    ? 'Sin número'
    : (repetido ? `Número repetido en este grupo (#${numero})` : `Número en este grupo: #${numero}`);
  return (
    <div
      title={titulo}
      className={cn(
        'font-bold rounded shrink-0 border',
        tam === 'sm' ? 'text-xs px-2 py-0.5' : 'text-sm px-2 py-1',
        sinNumero && 'border-dashed border-muted-foreground/50 text-muted-foreground',
        !sinNumero && !repetido && 'bg-primary text-primary-foreground border-transparent',
        repetido && 'bg-destructive text-destructive-foreground border-transparent',
      )}
    >
      {sinNumero ? '—' : `#${numero}`}
    </div>
  );
};

const SECTION_TABS = [
  { id: 'entrenamientos', label: 'Entrenamientos' },
  { id: 'torneos', label: 'Torneos' },
  { id: 'jugadores', label: 'Jugadores' }
];

const sanitizeCategoryIds = (ids = []) =>
  Array.from(
    new Set(
      (Array.isArray(ids) ? ids : [])
        .map((value) => (value ?? '').toString().trim())
        .filter(Boolean)
    )
  );

const formatCategoryRangeLabel = (categoria = {}, sportFallback = DEFAULT_SPORT) => {
  const deporte = normalizeSportValue(categoria.deporte || sportFallback);
  const currentYear = new Date().getFullYear();

  if (deporte === 'tackle') {
    const supDay = categoria.tackle_limite_sup_dia;
    const supMonth = categoria.tackle_limite_sup_mes;
    const supAge = categoria.tackle_edad_sup;
    const infDay = categoria.tackle_limite_inf_dia;
    const infMonth = categoria.tackle_limite_inf_mes;
    const infAge = categoria.tackle_edad_inf;
    const pesoMax = categoria.tackle_peso_max_kg;

    const supYear = typeof supAge === 'number' ? currentYear - supAge : null;
    const infYear = typeof infAge === 'number' ? currentYear - infAge : null;

    const formatDate = (day, month) => {
      if (!day || !month) return null;
      return `${String(day).padStart(2, '0')}/${String(month).padStart(2, '0')}`;
    };

    const supDate = formatDate(supDay, supMonth);
    const infDate = formatDate(infDay, infMonth);
    const parts = [];

    if (supYear || supDate) {
      const label = [
        'Límite superior:',
        supDate ? supDate : null,
        supYear ? `(nac. máx ${supYear})` : null
      ]
        .filter(Boolean)
        .join(' ');
      parts.push(label);
    }

    if (infYear || infDate) {
      const label = [
        'Límite inferior:',
        infDate ? infDate : null,
        infYear ? `(nac. mín ${infYear})` : null
      ]
        .filter(Boolean)
        .join(' ');
      parts.push(label);
    }

    if (typeof pesoMax === 'number') {
      parts.push(`Peso máx ${pesoMax} kg`);
    }

    return parts.length > 0 ? parts.join(' | ') : 'Rango por definir';
  }

  const { anio_inicio, anio_fin, edad_min, edad_max } = categoria;
  const hasYearMax = typeof anio_inicio === 'number';
  const hasYearMin = typeof anio_fin === 'number';

  if (hasYearMin || hasYearMax) {
    const yearMax = typeof anio_inicio === 'number' ? anio_inicio : null;
    const yearMin = typeof anio_fin === 'number' ? anio_fin : null;
    if (hasYearMin && hasYearMax) {
      if (yearMin === yearMax) return `Nacidos en ${yearMin}`;
      return `Nacidos ${yearMin}-${yearMax}`;
    }
    if (hasYearMin) return `Nacidos desde ${yearMin}`;
    return `Nacidos hasta ${yearMax}`;
  }

  const hasEdadMin = typeof edad_min === 'number';
  const hasEdadMax = typeof edad_max === 'number';
  if (hasEdadMin || hasEdadMax) {
    const derivedInicio = hasEdadMax ? currentYear - edad_max : null;
    const derivedFin = hasEdadMin ? currentYear - edad_min : null;

    if (derivedInicio !== null || derivedFin !== null) {
      const yearMin = derivedInicio !== null ? derivedInicio : null;
      const yearMax = derivedFin !== null ? derivedFin : null;
      if (yearMin !== null && yearMax !== null) {
        if (yearMin === yearMax) return `Nacidos en ${yearMin}`;
        return `Nacidos ${yearMin}-${yearMax}`;
      }
      if (yearMin !== null) return `Nacidos desde ${yearMin}`;
      return `Nacidos hasta ${yearMax}`;
    }

    if (hasEdadMin && hasEdadMax) {
      if (edad_min === edad_max) return `${edad_min} años`;
      return `${edad_min}-${edad_max} años`;
    }
    if (hasEdadMin) return `Desde ${edad_min} años`;
    return `Hasta ${edad_max} años`;
  }

  return 'Rango por definir';
};
const GroupDetails = () => {
  const { groupId, seccion } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { staff } = useAuth();
  const { org } = useOrganization();
  const { role, isAdmin, isHeadCoach, isManager, isPlatformAdmin, canTakeAttendance } = usePermissions();
  const { confirm, alert, ConfirmDialog } = useConfirmDialog();
  
  const [group, setGroup] = useState(null);
  const [players, setPlayers] = useState([]);
  const [estadoParticipacion, setEstadoParticipacion] = useState({});
  const [coaches, setCoaches] = useState([]);
  const [seriesEntrenamiento, setSeriesEntrenamiento] = useState([]);
  const [groupTorneos, setGroupTorneos] = useState([]);
  const [loadingTorneos, setLoadingTorneos] = useState(false);
  const [showElegirTorneo, setShowElegirTorneo] = useState(false);
  const [torneoParaInscribir, setTorneoParaInscribir] = useState(null);
  const [groupPrograms, setGroupPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [categorias, setCategorias] = useState([]);
  const [showAssignAdminStaff, setShowAssignAdminStaff] = useState(false);













  const [selectedAdminStaffId, setSelectedAdminStaffId] = useState('');
  const [selectedAdminStaffRole, setSelectedAdminStaffRole] = useState('Coach');
  const [selectedAdminTarjeton, setSelectedAdminTarjeton] = useState(false);
  const [assigningAdminStaff, setAssigningAdminStaff] = useState(false);
  const [showRemoveStaff, setShowRemoveStaff] = useState(false);
  const [selectedStaffToRemove, setSelectedStaffToRemove] = useState('');
  const [removingStaffMember, setRemovingStaffMember] = useState(false);
  const activeSection = SECTION_TABS.some((t) => t.id === seccion)
    ? seccion
    : SECTION_TABS[0].id;

  // `replace` a proposito: recorrer las tres pestanas no debe obligar a pulsar "atras"
  // tres veces para salir del grupo.
  const setActiveSection = useCallback(
    (id) => navigate(`/groups/${groupId}/${id}`, { replace: true }),
    [navigate, groupId],
  );
  const [showRosterModal, setShowRosterModal] = useState(false);
  const [rosterModalTab, setRosterModalTab] = useState('assign');
  const [statusFilter, setStatusFilter] = useState('todos');

  const getRoleBadgeVariant = useCallback((rol = '') => {
    const normalized = rol.toString().trim().toLowerCase().replace(/_/g, ' ');
    switch (normalized) {
      case 'head coach':
        return 'default';
      case 'assistant coach':
      case 'assistant':
      case 'asistente':
        return 'secondary';
      case 'manager':
        return 'warning';
      default:
        return 'outline';
    }
  }, []);


  // Edit group modal
  const [showEditGroup, setShowEditGroup] = useState(false);













  // Menus
  const [showPlayersMenu, setShowPlayersMenu] = useState(false);
  const [showStaffMenu, setShowStaffMenu] = useState(false);

  const canManageRoster = canTakeAttendance;
  const canManageStaffRole = isAdmin || isHeadCoach || isManager;
  // La identidad sale del staff que resuelve useAuth contra la identidad verificada del
  // JWT (auth_user_id / email), NUNCA de user_metadata: eso lo escribe el propio usuario
  // con supabase.auth.updateUser, asi que con un staff_id ajeno se hacia pasar por el
  // staff asignado a cualquier grupo. lib/services/auth.js ya retiro esa via por lo mismo.
  const currentStaffId = staff?.id ? staff.id.toString() : null;
  const toDateString = (date) => date.toISOString().slice(0, 10);


  const categoriasById = useMemo(() => {
    const map = new Map();
    categorias.forEach((cat) => {
      if (cat.id) {
        map.set(cat.id, cat);
      }
    });
    return map;
  }, [categorias]);

  const categoriasByName = useMemo(() => {
    const map = new Map();
    categorias.forEach((cat) => {
      if (cat.nombre) {
        map.set(cat.nombre.trim().toLowerCase(), cat);
      }
    });
    return map;
  }, [categorias]);

  const groupCategoria = useMemo(() => {
    if (!group) return null;
    if (group.categoria_id && categoriasById.has(group.categoria_id)) {
      return categoriasById.get(group.categoria_id);
    }
    if (group.categoria_base) {
      return categoriasByName.get(group.categoria_base.trim().toLowerCase()) || null;
    }
    return null;
  }, [group, categoriasById, categoriasByName]);

  const groupSport = useMemo(
    () => normalizeSportValue(group?.deporte || DEFAULT_SPORT),
    [group]
  );

  const groupCategoryBadges = useMemo(() => {
    if (!group) return [];

    const categoriesList =
      Array.isArray(group.categorias) && group.categorias.length > 0
        ? group.categorias.filter((categoria) => categoria?.activo !== false)
        : groupCategoria
          ? [groupCategoria]
          : [];

    if (categoriesList.length > 0) {
      return categoriesList
        .filter((categoria) => categoria && (categoria.id || categoria.nombre))
        .map((categoria) => ({
          id: categoria.id || categoria.nombre,
          nombre: categoria.nombre || 'Categoría',
          rango: formatCategoryRangeLabel(categoria, groupSport)
        }));
    }

    return [];
  }, [group, groupCategoria, groupSport]);
  const groupCategoryNameSet = useMemo(() => {
    const categoriesList = Array.isArray(group?.categorias)
      ? group.categorias.filter((categoria) => categoria?.activo !== false)
      : [];
    return new Set(
      categoriesList
        .map((categoria) => (categoria?.nombre || '').trim().toLowerCase())
        .filter(Boolean)
    );
  }, [group?.categorias]);
  const currentYear = useMemo(() => new Date().getFullYear(), []);

  const buildBoundaryDate = useCallback((year, month, day, boundType) => {
    if (typeof year !== 'number') return null;
    if (typeof month === 'number' && typeof day === 'number') {
      return new Date(year, month - 1, day);
    }
    return boundType === 'sup' ? new Date(year, 0, 1) : new Date(year, 11, 31);
  }, []);

  const categoriesForRules = useMemo(() => {
    if (Array.isArray(group?.categorias) && group.categorias.length > 0) {
      return group.categorias.filter((categoria) => categoria?.activo !== false);
    }
    return groupCategoria ? [groupCategoria] : [];
  }, [group?.categorias, groupCategoria]);

  const groupCategoryRules = useMemo(() => {
    return categoriesForRules
      .map((categoria) => {
        const deporte = normalizeSportValue(categoria?.deporte || group?.deporte || DEFAULT_SPORT);
        const nombre = categoria?.nombre || 'Categoria';

        if (deporte === 'tackle') {
          const supAge = typeof categoria.tackle_edad_sup === 'number' ? categoria.tackle_edad_sup : null;
          const infAge = typeof categoria.tackle_edad_inf === 'number' ? categoria.tackle_edad_inf : null;
          const supYear = supAge !== null ? currentYear - supAge : null;
          const infYear = infAge !== null ? currentYear - infAge : null;
          const supDate = buildBoundaryDate(
            supYear,
            categoria.tackle_limite_sup_mes,
            categoria.tackle_limite_sup_dia,
            'sup'
          );
          const infDate = buildBoundaryDate(
            infYear,
            categoria.tackle_limite_inf_mes,
            categoria.tackle_limite_inf_dia,
            'inf'
          );
          const hasAnyLimit = Boolean(supDate || infDate || supYear !== null || infYear !== null);
          return {
            nombre,
            deporte,
            supDate,
            infDate,
            hasLimits: hasAnyLimit
          };
        }

        const anioInicio = typeof categoria.anio_inicio === 'number' ? categoria.anio_inicio : null;
        const anioFin = typeof categoria.anio_fin === 'number' ? categoria.anio_fin : null;
        const edadMin = typeof categoria.edad_min === 'number' ? categoria.edad_min : null;
        const edadMax = typeof categoria.edad_max === 'number' ? categoria.edad_max : null;
        const resolvedAnioInicio =
          anioInicio === null && edadMin !== null ? currentYear - edadMin : anioInicio;
        const resolvedAnioFin =
          anioFin === null && edadMax !== null ? currentYear - edadMax : anioFin;
        const hasAnyLimit =
          resolvedAnioInicio !== null ||
          resolvedAnioFin !== null ||
          edadMin !== null ||
          edadMax !== null;
        return {
          nombre,
          deporte,
          anio_inicio: resolvedAnioInicio,
          anio_fin: resolvedAnioFin,
          hasLimits: hasAnyLimit
        };
      })
      .filter((rule) => rule.hasLimits);
  }, [categoriesForRules, group?.deporte, currentYear, buildBoundaryDate]);

  const outOfRangeLabel = 'fuera de categoría';

  const problemasDeNumero = useMemo(
    () => detectarNumeros(players.map((p) => numeroEnGrupo(p.grupo_assignment, p))),
    [players],
  );
  const numerosRepetidos = problemasDeNumero.repetidos;

  // Cuantos JUGADORES estan en conflicto, no cuantos numeros: "2 numeros repetidos" con
  // dos jugadores que comparten el 7 se lee mal si dice 1.
  const jugadoresConNumeroRepetido = useMemo(
    () => players.filter((p) => numerosRepetidos.has(numeroEnGrupo(p.grupo_assignment, p))).length,
    [players, numerosRepetidos],
  );
  const activeStaffAssignments = useMemo(() => {
    if (!group?.staff_grupos) return [];
    return group.staff_grupos.filter(rel => rel.activo !== false && rel.staff);
  }, [group]);

  // Las asignaciones llegan anidadas dentro del grupo, asi que no traen grupo_id: se lo
  // ponemos para poder preguntarle a la regla compartida en su mismo lenguaje.
  const misAsignacionesEnEsteGrupo = useMemo(() => {
    if (!currentStaffId || !group?.id) return [];
    return activeStaffAssignments
      .filter((assignment) => {
        const assignmentStaffId =
          assignment.staff_id || assignment.staff?.id || assignment.staff?.staff_id || null;
        return assignmentStaffId && assignmentStaffId.toString() === currentStaffId;
      })
      .map((assignment) => ({ ...assignment, grupo_id: group.id }));
  }, [activeStaffAssignments, currentStaffId, group?.id]);

  // La misma regla por grupo que MisJuegos e InscripcionDetail (lib/permisosJuegos,
  // espejo de puede_capturar_juegos): mira el rol EN el grupo y deja pasar al platform
  // admin, dos cosas que este gate decidia por su cuenta y al reves.
  const canManageThisGroup = useMemo(() => {
    // El platform admin no toma asistencia (canManageRoster es false para el sin
    // override), pero manda: la regla compartida lo deja pasar antes que a nadie.
    if (!isPlatformAdmin && !canManageRoster) return false;
    return puedeCapturarJuegos({
      rol: role,
      esPlatformAdmin: isPlatformAdmin,
      asignaciones: misAsignacionesEnEsteGrupo,
      grupoId: group?.id,
    });
  }, [canManageRoster, isPlatformAdmin, role, misAsignacionesEnEsteGrupo, group?.id]);

  const canManageStaff = canManageThisGroup && canManageStaffRole;
  const groupRestrictionType = useMemo(() => {
    const raw = (group?.tipo_restriccion || '').toString().trim().toLowerCase();
    if (raw === 'estricta' || raw === 'recomendada' || raw === 'sin_restriccion') {
      return raw;
    }
    return 'sin_restriccion';
  }, [group?.tipo_restriccion]);

  const openRosterModal = useCallback(
    async (tab = 'assign') => {
      if (!canManageThisGroup) {
        await alert('Acceso restringido', 'Solo el staff asignado puede gestionar jugadores de este grupo.');
        return;
      }
      setRosterModalTab(tab);
      setShowRosterModal(true);
    },
    [canManageThisGroup, alert]
  );

  useEffect(() => {
    if (!canManageThisGroup && (showPlayersMenu || showStaffMenu)) {
      setShowPlayersMenu(false);
      setShowStaffMenu(false);
    }
  }, [canManageThisGroup, showPlayersMenu, showStaffMenu]);

  const entrenamientosActivos = 0;

  const [serieAttendanceMap, setSerieAttendanceMap] = useState(new Map());

  const activeSeries = useMemo(() => {
    return (seriesEntrenamiento || []).filter((serie) => {
      if (serie.activo === true) return true;
      return (serie.estatus || '').toLowerCase() === 'activo';
    });
  }, [seriesEntrenamiento]);

  const trainingStats = useMemo(() => {
    if (!seriesEntrenamiento.length) return { total: 0, active: 0, completed: 0 };
    const active = activeSeries.length;
    const completed = seriesEntrenamiento.filter(
      (serie) => (serie.estatus || '').toLowerCase() === 'finalizado'
    ).length;
    return { total: seriesEntrenamiento.length, active, completed };
  }, [seriesEntrenamiento, activeSeries]);
  useEffect(() => {
    const loadAttendanceStats = async () => {
      if (!group?.id) {
        setSerieAttendanceMap(new Map());
        return;
      }

      const end = new Date();
      const start = new Date(end);
      start.setDate(start.getDate() - 90);

      const { data: statsData, error } = await getAttendanceStatsByGroup({
        groupId: group.id,
        startDate: toDateString(start),
        endDate: toDateString(end)
      });

      if (error) {
        console.error('Error cargando resumen de asistencias:', error);
        return;
      }

      const map = new Map();
      (statsData || []).forEach((row) => {
        const sesion = row.sesion || {};
        const serieId = sesion.serie_entrenamiento_id;
        if (!serieId) return;

        const existing = map.get(serieId) || {
          presentes: 0,
          ausentes: 0,
          retardos: 0,
          finishedIds: new Set()
        };

        const sesionId = sesion.id;
        const status = (sesion.estatus || '').toLowerCase();
        if (sesionId && status === 'terminado') {
          existing.finishedIds.add(sesionId);
        }

        const presente = row.presente;
        if (presente === 'exento') {
          // Exentos no cuentan para el porcentaje
        } else if (presente === true || presente === 'presente') {
          existing.presentes += 1;
        } else if (presente === 'retardo') {
          existing.retardos += 1;
        } else if (presente === false || presente === 'ausente' || presente === 'justificado') {
          existing.ausentes += 1;
        }

        map.set(serieId, existing);
      });

      const finalMap = new Map();
      map.forEach((stats, serieId) => {
        finalMap.set(serieId, {
          presentes: stats.presentes,
          ausentes: stats.ausentes,
          retardos: stats.retardos,
          finished: stats.finishedIds.size
        });
      });

      setSerieAttendanceMap(finalMap);
    };

    loadAttendanceStats();
  }, [group?.id]);

  const attendanceSummary = useMemo(() => {
    let finished = 0;
    let presentes = 0;
    let ausentes = 0;
    let retardos = 0;
    serieAttendanceMap.forEach((stats) => {
      finished += stats.finished;
      presentes += stats.presentes;
      ausentes += stats.ausentes;
      retardos += stats.retardos;
    });
    if (finished === 0) return null;
    const totalRegistros = presentes + ausentes + retardos;
    const pct = totalRegistros ? Math.round((presentes / totalRegistros) * 100) : 0;
    return { finished, presentes, ausentes, retardos, pct };
  }, [serieAttendanceMap]);

  const rosterSummary = useMemo(() => {
    const statuses = players.reduce(
      (acc, player) => {
        const status = (player.group_estatus || player.estatus || '').toLowerCase();
        if (status === 'activo') acc.active += 1;
        else if (status === 'pendiente') acc.pending += 1;
        else acc.inactive += 1;
        return acc;
      },
      { active: 0, pending: 0, inactive: 0 }
    );
    return {
      ...statuses,
      total: players.length,
      staff: activeStaffAssignments.length
    };
  }, [players, activeStaffAssignments.length]);

  const getSerieAttendanceSummary = useCallback(
    (serieId) => {
      const stats = serieAttendanceMap.get(serieId);
      if (!stats || stats.finished === 0) {
        return null;
      }
      const average = (value) => Math.round(value / stats.finished);

      return {
        finished: stats.finished,
        asistencia: average(stats.presentes),
        retardos: average(stats.retardos),
        ausencias: average(stats.ausentes)
      };
    },
    [serieAttendanceMap]
  );

  // La pantalla de "Cargando..." solo en la PRIMERA carga. Cada accion (inscribir,
  // editar, quitar, guardar) recargaba los datos con loading=true y la pantalla
  // entera se desmontaba: se perdian scroll y foco, se sentia como una recarga de la
  // pagina (el dueno, 2026-09-07). Las recargas siguientes son silenciosas.
  const cargadoRef = useRef(false);
  const loadGroupData = useCallback(async () => {
    try {
      if (!cargadoRef.current) setLoading(true);
      
      // Load group details
      const { data: groupData, error: groupError } = await getGroupById(groupId);
      if (groupError) throw groupError;
      
      setGroup(groupData);

      // Load group players
      const { data: playersData, error: playersError } = await getGroupPlayers(groupId);
      if (playersError) throw playersError;
      
      // Transform data to extract player info from jugadores_grupos
      const transformedPlayers = (playersData || []).map(playerGroup => ({
        ...playerGroup.jugadores,
        grupo_assignment: playerGroup, // Keep assignment info if needed
        group_estatus: playerGroup.estatus // Estatus del jugador EN EL GRUPO
      }));
      
      // Remove duplicates based on player ID
      const uniquePlayers = transformedPlayers.filter((player, index, array) => 
        array.findIndex(p => p.id === player.id) === index
      );
      
      console.log(
        `Jugadores originales: ${transformedPlayers.length}, únicos: ${uniquePlayers.length}`
      );
      if (transformedPlayers.length !== uniquePlayers.length) {
        console.warn('Jugadores duplicados detectados y removidos');
      }
      
      setPlayers(uniquePlayers);

      // Indicadores de participacion (F2): que le falta a cada jugador, visible
      // donde el coach trabaja. Es contexto: si falla, la lista sigue sirviendo
      // sin badges en vez de tumbar la pantalla del grupo.
      getEstadoJugadoresGrupo(groupId)
        .then(({ data }) => setEstadoParticipacion(data?.porJugador || {}))
        .catch(() => setEstadoParticipacion({}));

      // Load coaches if role can manage staff
      if (canManageStaffRole) {
        const { data: coachesData, error: coachesError } = await getAllCoaches();
        if (coachesError) throw coachesError;
        setCoaches(coachesData || []);
      }

      // Load series de entrenamiento
      const { data: seriesData, error: seriesError } = await getSeriesEntrenamiento(groupId);
      if (seriesError) {
        console.error('Error loading series:', seriesError);
      } else {
        setSeriesEntrenamiento(seriesData || []);
      }

    } catch (error) {
      console.error('Error loading group data:', error);
      await alert('Error', 'Error cargando información del grupo');
    } finally {
      setLoading(false);
      cargadoRef.current = true;
    }
  }, [groupId, canManageStaffRole, alert]);

useEffect(() => {
  if (groupId) {
    loadGroupData();
  }
}, [groupId, loadGroupData]);

useEffect(() => {
  setActiveSection(SECTION_TABS[0].id);
}, [groupId]);

useEffect(() => {
  const fetchCategorias = async () => {
    try {
      const { data, error } = await getCategorias();
      if (error) throw error;
      setCategorias(data || []);
    } catch (error) {
      console.error('Error loading categorias:', error);
    }
  };
  fetchCategorias();
}, []);

  const loadGroupTorneos = useCallback(async () => {
    if (!groupId) return;
    try {
      setLoadingTorneos(true);
      // Modelo nuevo (Fase 6): trae record W-L-D + inscripcion_id
      const { data: inscripciones, error } = await getInscripcionesByGrupoIds([groupId]);
      if (error) throw error;
      const formatted = await Promise.all(
        (inscripciones || [])
          .filter((insc) => insc.torneos)
          .map(async (insc) => {
            const torneo = insc.torneos;
            const stats = {
              total_juegos: insc.partidos_jugados || 0,
              victorias: insc.ganados || 0,
              derrotas: insc.perdidos || 0,
              empates: insc.empatados || 0,
              puntos_favor: insc.puntos_favor || 0,
              puntos_contra: insc.puntos_contra || 0
            };
            let asistencia = null;
            try {
              asistencia = await getAsistenciaGrupoEnTorneo(torneo.id, groupId);
            } catch (e) { /* opcional */ }
            return {
              ...torneo,
              // El select devuelve la liga como `ligas` (nombre del join) y la fila
              // leia `torneo.liga`: la linea "Liga: X" y el logo nunca se pintaban.
              liga: torneo.liga || torneo.ligas || null,
              inscripcion_id: insc.id,
              stats,
              asistencia
            };
          })
      );
      setGroupTorneos(formatted);
    } catch (error) {
      console.error('Error loading torneos del grupo:', error);
    } finally {
      setLoadingTorneos(false);
    }
  }, [groupId]);

  useEffect(() => {
    loadGroupTorneos();
  }, [loadGroupTorneos]);


  const loadGroupPrograms = useCallback(async () => {}, []);
  const handleEditGroup = () => {
    if (!group) return;
    setShowEditGroup(true);
  };
























  const handleOpenAssignAdminStaff = async () => {
    if (!canManageStaff) {
      await alert('Acceso restringido', 'No puedes asignar staff en este grupo.');
      return;
    }
    setSelectedAdminStaffId('');
    assignStaffGuard.resetDirty();
    setShowAssignAdminStaff(true);
  };

  const handleOpenRemoveStaff = async () => {
    if (!canManageStaff) {
      await alert('Acceso restringido', 'No puedes remover staff en este grupo.');
      return;
    }
    setSelectedStaffToRemove('');
    removeStaffGuard.resetDirty();
    setShowRemoveStaff(true);
  };

  const handleOpenTarjetonPreview = useCallback(
    async (torneo) => {
      if (!torneo?.id || !groupId) {
        await alert('Error', 'No se pudo abrir el tarjetón.');
        return;
      }
      navigate(`/tarjeton/preview?grupoId=${groupId}&torneoId=${torneo.id}`);
    },
    [groupId, navigate, alert]
  );

  const closeAssignAdminStaff = () => {
    setShowAssignAdminStaff(false);
    setSelectedAdminStaffId('');
    setSelectedAdminStaffRole('Coach');
    setSelectedAdminTarjeton(false);
  };

  const assignStaffGuard = useUnsavedGuard({ onClose: closeAssignAdminStaff });

  const closeRemoveStaff = () => {
    setShowRemoveStaff(false);
    setSelectedStaffToRemove('');
  };

  const removeStaffGuard = useUnsavedGuard({ onClose: closeRemoveStaff });

  const handleAssignAdminStaff = async () => {
    if (!selectedAdminStaffId) {
      await alert('Error', 'Selecciona un miembro del staff');
      return;
    }

    try {
      setAssigningAdminStaff(true);
      const alreadyAssigned = activeStaffAssignments.find(
        (assignment) =>
          assignment.staff_id === selectedAdminStaffId ||
          assignment.staff?.id === selectedAdminStaffId
      );

      const normalizedRole = selectedAdminStaffRole.trim().toLowerCase();
      const isAssistantRole = normalizedRole.includes('asistente') || normalizedRole.includes('assistant');
      const isManagerRole = normalizedRole.includes('manager');
      const payload = {
        tarjeton_entrenador: selectedAdminTarjeton === true && !isAssistantRole && !isManagerRole,
        tarjeton_asistente: selectedAdminTarjeton === true && isAssistantRole
      };

      if (selectedAdminTarjeton && isManagerRole) {
        await alert('Error', 'El rol Manager no puede aparecer en el tarjeton.');
        setAssigningAdminStaff(false);
        return;
      }

      if (selectedAdminTarjeton) {
        const hasCoach = activeStaffAssignments.some(
          (assignment) =>
            assignment.staff_id !== selectedAdminStaffId &&
            assignment.tarjeton_entrenador === true
        );
        const hasAssistant = activeStaffAssignments.some(
          (assignment) =>
            assignment.staff_id !== selectedAdminStaffId &&
            assignment.tarjeton_asistente === true
        );
        if (payload.tarjeton_entrenador && hasCoach) {
          await alert('Error', 'Solo puede haber un entrenador en el tarjeton.');
          setAssigningAdminStaff(false);
          return;
        }
        if (payload.tarjeton_asistente && hasAssistant) {
          await alert('Error', 'Solo puede haber un asistente en el tarjeton.');
          setAssigningAdminStaff(false);
          return;
        }
      }

      if (alreadyAssigned) {
        const { error } = await updateStaffRole(
          groupId,
          selectedAdminStaffId,
          selectedAdminStaffRole,
          payload
        );
        if (error) throw error;
      } else {
        const { error } = await assignStaffToGroup(
          groupId,
          selectedAdminStaffId,
          selectedAdminStaffRole,
          payload
        );
        if (error) throw error;
      }

      await loadGroupData();
      assignStaffGuard.resetDirty();
      closeAssignAdminStaff();
      await alert('Listo', 'Staff asignado correctamente');
    } catch (error) {
      console.error('Error assigning staff:', error);
      await alert('Error', 'No se pudo asignar el staff');
    } finally {
      setAssigningAdminStaff(false);
    }
  };

  const handleRemoveStaff = async () => {
    if (!selectedStaffToRemove) {
      await alert('Error', 'Selecciona al staff a remover');
      return;
    }
    try {
      setRemovingStaffMember(true);
      const { error } = await removeStaffFromGroup(groupId, selectedStaffToRemove);
      if (error) throw error;
      await loadGroupData();
      removeStaffGuard.resetDirty();
      closeRemoveStaff();
      await alert('Listo', 'Staff removido del grupo');
    } catch (error) {
      console.error('Error removing staff:', error);
      await alert('Error', 'No se pudo remover al staff');
    } finally {
      setRemovingStaffMember(false);
    }
  };


















































  const handleDeleteGroup = async () => {
    const confirmed = await confirm(
      'Desactivar grupo',
      '¿Desactivar este grupo? Se marcará como inactivo y ya no aparecerá en las listas activas.',
      { destructive: true }
    );

    if (!confirmed) return;

    setSyncing(true);
    try {
      const { data, error, type } = await deleteGroup(groupId);
      if (error) throw error;

      await alert('Grupo actualizado', 'El grupo ha sido marcado como inactivo.');

      // Navigate back to groups list
      navigate('/groups');
    } catch (error) {
      console.error('Error deleting group:', error);
      await alert('Error', 'Error eliminando grupo');
    } finally {
      setSyncing(false);
    }
  };

  // La orquestacion de la foto (a quien, subiendo, exito, cerrar) vivia aqui como
  // estado suelto y era la unica puerta para cambiarla en todo el sistema. Ahora es un
  // hook, y el perfil del jugador usa el mismo.
  const foto = usePhotoUpload({ onDone: loadGroupData });

  // Numeros de camiseta del grupo. El editor no trae boton de guardar: deja su funcion
  // en saveRef y quien lo monta decide cuando llamarla.
  const [numerosAbierto, setNumerosAbierto] = useState(false);
  const [numerosEstado, setNumerosEstado] = useState({ hayRepetidos: false });
  const [guardandoNumeros, setGuardandoNumeros] = useState(false);
  const guardarNumerosRef = useRef(null);

  const guardarNumeros = async () => {
    if (!guardarNumerosRef.current) return;
    setGuardandoNumeros(true);
    const { error } = await guardarNumerosRef.current();
    setGuardandoNumeros(false);
    if (error) { await alert('Error', 'No se pudieron guardar los numeros'); return; }
    setNumerosAbierto(false);
    await loadGroupData();
  };

  const handleCopyImageUrl = async (imageUrl) => {
    try {
      await navigator.clipboard.writeText(imageUrl);
      await alert('Listo', 'URL de la foto copiada al portapapeles');
    } catch (error) {
      console.error('Error copying to clipboard:', error);
      await alert('Error', 'No se pudo copiar la URL');
    }
  };

  const handleDownloadImage = async (imageUrl, playerName) => {
    try {
      const response = await fetch(imageUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      
      const a = document.createElement('a');
      a.href = url;
      a.download = `${playerName}_foto.jpg`;
      document.body.appendChild(a);
      a.click();
      
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      await alert('Listo', 'Foto descargada correctamente');
    } catch (error) {
      console.error('Error downloading image:', error);
      await alert('Error', 'No se pudo descargar la foto');
    }
  };

  const handleStartTraining = () => {
    // Navegar a la pantalla de asistencia en modo entrenamiento
    navigate(`/attendance/${group.id}`, {
      state: {
        group: group,
        mode: 'entrenamiento',
        fecha: getMexicoDateString()
      }
    });
  };

  const handleGoToReport = () => {
    if (!group) return;
    const today = new Date();
    const month = today.getMonth() + 1;
    const year = today.getFullYear();
    navigate(`/entrenamientos/asistencia-mensual?grupo=${group.id}&mes=${month}&year=${year}`);
  };

  const handleGoToAgenda = () => {
    if (!group) return;
    navigate('/agenda', {
      state: {
        groupIds: [group.id]
      }
    });
  };

  const handleViewTorneo = (torneoId) => {
    if (!torneoId) return;
    navigate(`/torneos/${torneoId}`);
  };

  const handleCreateJuego = (torneoId) => {
    if (!group?.id || !torneoId) return;
    navigate('/torneos/juegos', {
      state: {
        torneoId,
        grupoId: group.id,
        abrirNuevoJuego: true
      }
    });
  };

  const handleViewRol = (torneoId) => {
    if (!group) return;
    navigate('/torneos/juegos', {
      state: {
        viewBy: 'grupo',
        groupId: group.id,
        torneoId: torneoId || null
      }
    });
  };

  const formatDate = (date) => formatLocalDate(date, "EEEE d 'de' MMMM yyyy");
  const formatDateRange = (start, end) => {
    if (!start && !end) return 'Sin fecha';
    if (start && end) {
      return `${formatLocalDate(start, "d MMM yyyy")} - ${formatLocalDate(end, "d MMM yyyy")}`;
    }
    return formatLocalDate(start || end, "d MMM yyyy");
  };



  const getInitials = (name = '') => {
    if (!name) return 'ST';
    const parts = name.trim().split(/\s+/);
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  };

  const formatCoachRole = (role = 'coach') => {
    const value = role.trim().toLowerCase().replace(/_/g, ' ');
    if (value === 'principal' || value.includes('head')) return 'Head Coach';
    if (value === 'asistente' || value.includes('asistente') || value.includes('assistant')) return 'Coach asistente';
    if (value === 'manager' || value.includes('manager')) return 'Manager';
    return 'Coach';
  };

  const filteredGroupPlayers = useMemo(() => {
    if (statusFilter === 'todos') return players;
    return players.filter((player) => {
      const playerStatus = (player.group_estatus || player.estatus || '').toLowerCase();
      return playerStatus === statusFilter;
    });
  }, [players, statusFilter]);

  // El conteo del tab es el total, no el filtrado: el filtro de estatus vive
  // DENTRO de Jugadores, y un tab que cambia de numero al filtrar se lee como si
  // el grupo hubiera cambiado de tamano.
  const tabCounts = useMemo(() => ({
    entrenamientos: activeSeries.length,
    torneos: groupTorneos.length,
    jugadores: players.length
  }), [activeSeries.length, groupTorneos.length, players.length]);

// Franja de estadisticas, no tarjetas anidadas. Tres cards dentro de otra card
// generaban tres marcos compitiendo, y el icono se comia el ancho de una columna
// de ~100px en telefono hasta truncar la etiqueta ("Hor...", "Asi...").
// El numero va primero porque es el dato; la etiqueta debajo lo nombra.
const StatChip = ({ label, value, helper, tone = 'default' }) => {
    const valueColor = {
      default: 'text-foreground',
      success: 'text-emerald-600',
      warning: 'text-amber-600'
    }[tone] || 'text-foreground';

    return (
      <div className="min-w-0 px-1 text-center">
        <div className={`text-2xl font-bold leading-none tabular-nums ${valueColor}`}>{value}</div>
        <div className="mt-1 text-xs font-medium leading-tight">{label}</div>
        {helper && (
          // Era text-[11px]: un cuarto tamano fuera de la escala, a 1px del text-xs
          // de la etiqueta. Se lee como error de render, no como jerarquia.
          <div className="mt-0.5 text-xs leading-snug text-muted-foreground">{helper}</div>
        )}
      </div>
    );
  };

  // Las dos casas de la pestaña Torneos. `sinMundo` (la org no cargo todavia, o a la
  // fila le falta la organizadora) se ensena con los internos mientras tanto: son
  // inscripciones de este club y esconderlas seria peor que acomodarlas un instante.
  const { torneosInternos, torneosRed } = useMemo(() => {
    const partes = partirPorMundo(groupTorneos, org?.id);
    return {
      torneosInternos: [...partes[MUNDO_INTERNO], ...partes.sinMundo],
      torneosRed: partes[MUNDO_RED],
    };
  }, [groupTorneos, org?.id]);

  // Una fila de torneo, igual en las dos casas: lo que cambia es la seccion, no la
  // fila. Ya no lleva distintivo de mundo: la seccion lo dice.
  const renderFilaTorneo = (torneo) => (
    <div
      key={torneo.id}
      className="px-4 py-4 hover:bg-muted/50 transition-colors sm:px-6"
    >
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 min-w-0">
            {torneo.liga?.logo_url && (
              <img
                src={torneo.liga.logo_url}
                alt={torneo.liga?.nombre ? `Logo ${torneo.liga.nombre}` : 'Logo liga'}
                className="h-6 w-6 rounded-full object-cover border border-border flex-shrink-0"
              />
            )}
            {/* h5: la seccion es h3 y el bloque de temporada h4. */}
            <h5 className="text-foreground font-medium truncate">{torneo.nombre}</h5>
            <Badge
              variant="outline"
              className={`hidden sm:inline-flex ${ETAPA_STYLES[etapaDeTorneo(torneo)] || ''}`}
            >
              {ETAPA_LABELS[etapaDeTorneo(torneo)] || etapaDeTorneo(torneo)}
            </Badge>
            <span className="hidden sm:inline-flex bg-muted text-foreground px-2 py-1 rounded font-medium capitalize text-xs">
              {torneo.tipo_torneo || 'torneo'}
            </span>
          </div>
          <p className="text-xs text-muted-foreground">
            {formatDateRange(torneo.fecha_inicio, torneo.fecha_fin)}
          </p>
          {torneo.liga?.nombre && (
            <p className="text-xs text-muted-foreground mt-1">
              Liga: {torneo.liga.nombre}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2 sm:hidden">
          <Badge
            variant="outline"
            className={ETAPA_STYLES[etapaDeTorneo(torneo)] || ''}
          >
            {ETAPA_LABELS[etapaDeTorneo(torneo)] || etapaDeTorneo(torneo)}
          </Badge>
          <span className="bg-muted text-foreground px-2 py-1 rounded font-medium capitalize text-xs">
            {torneo.tipo_torneo || 'torneo'}
          </span>
        </div>
      </div>
      {/* Eran 4 botones del mismo peso en 2x2. "Nuevo juego" es lo que
          de verdad se hace desde aqui; el resto es navegacion.
          Sin flex-1: estiraba el primario a todo el ancho en movil y
          pesaba mas que la tarjeta entera. */}
      <div className="mt-3 flex items-center gap-2">
          {canManageThisGroup ? (
            <>
              {/* "Nuevo juego" en todo torneo que capture el club (organizadora = mi
                  org). En uno de la red el rol baja solo y el trigger _juegos_guard
                  rechaza la escritura: ahi el boton no prometeria nada. */}
              {clubCapturaEsteTorneo(torneo, org?.id) ? (
                <Button
                  size="sm"
                  className="h-8 text-xs"
                  onClick={() => handleCreateJuego(torneo.id)}
                >
                  <Plus className="h-3.5 w-3.5 mr-1" />
                  Nuevo juego
                </Button>
              ) : (
                <Button
                  size="sm"
                  className="h-8 text-xs"
                  onClick={() => navigate(`/torneos-myklubb/${torneo.id}`, { state: { nombre: torneo.nombre } })}
                >
                  <CalendarDays className="h-3.5 w-3.5 mr-1" />
                  Ver torneo
                </Button>
              )}
              {/* Sube del menu a accion visible: imprimir el tarjeton
                  es rutina de dia de juego, no una accion terciaria. */}
              <Button
                variant="outline"
                size="sm"
                className="h-8 text-xs"
                onClick={() => handleOpenTarjetonPreview(torneo)}
              >
                <FileText className="h-3.5 w-3.5 mr-1" />
                Tarjetón
              </Button>
            </>
          ) : (
            <Button
              size="sm"
              className="h-8 text-xs"
              onClick={() => handleViewTorneo(torneo.id)}
            >
              Ver torneo
            </Button>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="h-8 w-8 p-0 shrink-0" title="Más acciones">
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {canManageThisGroup && (
                <DropdownMenuItem onClick={() => handleViewTorneo(torneo.id)}>
                  <Trophy className="h-4 w-4" />
                  Ver torneo
                </DropdownMenuItem>
              )}
              {torneo.inscripcion_id && (
                <DropdownMenuItem
                  onClick={() => navigate(`/torneos/${torneo.id}/inscripciones/${torneo.inscripcion_id}`)}
                >
                  <ClipboardList className="h-4 w-4" />
                  Ver inscripción
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
      </div>
      {(torneo.stats || torneo.asistencia) && (
        <div className="mt-3 grid grid-cols-2 gap-2 text-xs text-muted-foreground sm:flex sm:flex-wrap sm:items-center sm:gap-4">
          {torneo.stats && (
            <>
              <span className="font-medium">Partidos: {torneo.stats.total_juegos}</span>
              <span>G/P/E: {torneo.stats.victorias}-{torneo.stats.derrotas}-{torneo.stats.empates}</span>
              <span>PF: {torneo.stats.puntos_favor}</span>
              <span>PC: {torneo.stats.puntos_contra}</span>
            </>
          )}
          {torneo.asistencia && (
            <>
              <span className="font-medium">Asistencia: {torneo.asistencia.porcentaje}%</span>
              <span>({torneo.asistencia.presentes}/{torneo.asistencia.total})</span>
            </>
          )}
        </div>
      )}
    </div>
  );

  const getStatusClass = (status) => {
    switch (status?.toLowerCase()) {
      case 'programado': return 'bg-blue-100 text-blue-800';
      case 'en curso': return 'bg-green-100 text-green-800';
      case 'terminado': return 'bg-gray-100 text-gray-800';
      case 'cancelado': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-muted-foreground';
    }
  };

  const getStatusVariant = (status) => {
    switch (status?.toLowerCase()) {
      case 'activo': return 'success';
      case 'inactivo': return 'warning';
      case 'suspendido': return 'error';
      case 'eliminado': return 'error';
      default: return 'secondary';
    }
  };

  if (loading) {
    return (
      <DetailLayout
        title="Cargando..."
        subtitle="Cargando información..."
        breadcrumbs={[
          { label: 'Grupos', path: '/groups' },
          { label: 'Detalle' }
        ]}
        backPath="/groups"
      >
        <p className="text-muted-foreground text-sm py-8 text-center">Cargando información del grupo...</p>
      </DetailLayout>
    );
  }

  if (!group) {
    return (
      <DetailLayout
        title="Grupo no encontrado"
        subtitle="Error"
        breadcrumbs={[
          { label: 'Grupos', path: '/groups' },
          { label: 'Detalle' }
        ]}
        backPath="/groups"
      >
        <div className="flex items-center justify-center py-20">
          <div className="text-center px-6">
            <h3 className="text-foreground font-semibold text-lg mb-2">Grupo no encontrado</h3>
            <p className="text-muted-foreground text-sm max-w-sm mb-4">
              El grupo que buscas no existe o no tienes permisos para verlo
            </p>
            <Button onClick={() => navigate('/groups')}>
              Volver a Grupos
            </Button>
          </div>
        </div>
      </DetailLayout>
    );
  }
  return (
    <DetailLayout
      title={group.nombre_del_grupo}
      breadcrumbs={[
        { label: 'Grupos', path: '/groups' },
        { label: group.nombre_del_grupo }
      ]}
      backPath="/groups"
      refreshAction={loadGroupData}
    >
      <div className="p-4 w-full max-w-full min-w-0 overflow-hidden">
        {/* Group Info Header */}
        <Card className="mb-6 w-full max-w-full">
          <CardHeader className="pb-4">
            {/* Sin CardTitle: el breadcrumb de arriba ya dice "Grupos > U10 Demo"
                (DetailLayout solo usa `title` cuando NO hay breadcrumbs), asi que
                repetir el nombre aqui era puro eco.
                Tampoco va la linea del coach: el bloque de Staff de abajo lo lista
                con su rol, que es mas completo. */}
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0 space-y-2 pt-0.5">
                {/* Rama y tipo eran texto plano con un "·" de separador, metidos
                    entre badges. Eso alternaba badge / texto / badge / texto y era
                    el origen de la sensacion de "muchas fuentes distintas".
                    Son atributos categoricos igual que la categoria, asi que se
                    presentan igual: tags. El unico texto suelto del bloque queda
                    siendo la descripcion. */}
                {/* Una sola tira de tags que fluye, en vez de dos filas con
                    espaciados distintos (gap-1.5 y gap-2). Todo lo categorico del
                    grupo vive aqui: estado, atributos y categorias.
                    Los dos spans del badge de categoria traian text-[0.7rem] y
                    text-[0.65rem] -- dos tamanos inventados con menos de 1px de
                    diferencia. Ahora heredan el tamano del Badge y se distinguen
                    por PESO y color, que es lo que separa un nombre de su dato. */}
                <div className="flex flex-wrap items-center gap-1.5">
                  <Badge variant={getStatusVariant(group.estatus)} className="shrink-0">
                    {group.estatus}
                  </Badge>
                  {[group.rama, group.tipo_de_grupo].filter(Boolean).map((attr) => (
                    <Badge key={attr} variant="outline" className="font-normal shrink-0">
                      {attr}
                    </Badge>
                  ))}
                  {groupCategoryBadges.length === 0 ? (
                    <Badge variant="outline" className="font-normal shrink-0">
                      Sin categorías asignadas
                    </Badge>
                  ) : (
                    groupCategoryBadges.map((categoria) => (
                      <Badge
                        key={categoria.id}
                        variant="secondary"
                        className="flex shrink-0 items-center gap-1.5 font-normal"
                      >
                        <span className="font-semibold leading-none">
                          {categoria.nombre}
                        </span>
                        <span className="leading-none text-muted-foreground">
                          {categoria.rango}
                        </span>
                      </Badge>
                    ))
                  )}
                </div>
                {group.descripcion && (
                  // La descripcion es el dato menos importante del bloque: baja a
                  // text-xs y se corta a 2 lineas para que una nota larga no
                  // empuje la franja de estadisticas fuera de la vista.
                  <p className="text-xs text-muted-foreground line-clamp-2">
                    {group.descripcion}
                  </p>
                )}
              </div>

              {/* Acciones compactas arriba a la derecha: en movil solo el icono,
                  antes ocupaban media pantalla cada una. */}
              <div className="flex items-center gap-1.5 shrink-0">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleGoToAgenda}
                  className="h-8 w-8 p-0 sm:w-auto sm:px-3 sm:text-xs"
                  aria-label="Ver agenda"
                  title="Ver agenda"
                >
                  <CalendarDays className="h-3.5 w-3.5 sm:mr-1" strokeWidth={1.5} />
                  <span className="hidden sm:inline">Agenda</span>
                </Button>
                {canManageThisGroup && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      handleEditGroup();
                    }}
                    className="h-8 w-8 p-0 sm:w-auto sm:px-3 sm:text-xs"
                    aria-label="Editar grupo"
                    title="Editar grupo"
                  >
                    <Edit className="h-3.5 w-3.5 sm:mr-1" strokeWidth={1.5} />
                    <span className="hidden sm:inline">Editar</span>
                  </Button>
                )}
              </div>
            </div>

            {/* Franja separada por lineas, sin tarjetas anidadas ni iconos */}
            <div className="mt-5 grid grid-cols-3 divide-x border-t pt-4">
              <StatChip
                label="Horarios"
                value={`${trainingStats.active}/${trainingStats.total}`}
                helper={`${trainingStats.completed} finalizadas`}
              />
              <StatChip
                label="Asistencia"
                value={attendanceSummary ? `${attendanceSummary.pct}%` : '—'}
                tone={attendanceSummary && attendanceSummary.pct >= 85 ? 'success' : 'warning'}
                helper={
                  attendanceSummary
                    ? `${attendanceSummary.presentes} pres · ${attendanceSummary.ausentes} fal`
                    : 'Sin registros'
                }
              />
              <StatChip
                label="Roster"
                value={`${rosterSummary.active}/${rosterSummary.total || 0}`}
                helper={`${rosterSummary.pending} pend · ${rosterSummary.inactive} inact`}
              />
            </div>
          </CardHeader>

          <div className="px-6 pb-6">
            {/* Todo en una linea: sin flex-wrap y con los botones a icono en movil.
                Antes envolvian a un segundo renglon y se veian enormes. */}
            <div className="flex items-center justify-between gap-2 mb-3">
              <div className="flex items-center gap-2 min-w-0">
                <Shield className="h-4 w-4 shrink-0 text-muted-foreground" />
                <span className="text-sm font-semibold whitespace-nowrap">Staff</span>
                {activeStaffAssignments.length > 0 && (
                  <Badge variant="secondary" className="text-xs">
                    {activeStaffAssignments.length}
                  </Badge>
                )}
              </div>
              {canManageStaff && (
                <div className="flex items-center gap-1.5 shrink-0">
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-7 w-7 p-0 sm:w-auto sm:px-2.5 sm:text-xs"
                    onClick={handleOpenAssignAdminStaff}
                    aria-label="Asignar staff"
                    title="Asignar staff"
                  >
                    <Plus className="h-3.5 w-3.5 sm:mr-1" />
                    <span className="hidden sm:inline">Asignar</span>
                  </Button>
                  {activeStaffAssignments.length > 0 && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-7 w-7 p-0 sm:w-auto sm:px-2.5 sm:text-xs"
                      onClick={handleOpenRemoveStaff}
                      aria-label="Remover staff"
                      title="Remover staff"
                    >
                      <UserMinus className="h-3.5 w-3.5 sm:mr-1" />
                      <span className="hidden sm:inline">Remover</span>
                    </Button>
                  )}
                </div>
              )}
            </div>
            {activeStaffAssignments.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                No hay staff asignado para este grupo actualmente.
              </p>
            ) : (
              <div className="flex flex-wrap gap-3">
                {activeStaffAssignments.map((assignment) => (
                  <div
                    key={assignment.id}
                    className="flex items-center gap-2 rounded-full border bg-background/80 px-3 py-1.5 shadow-sm"
                  >
                    <div className="flex h-8 w-8 items-center justify-center rounded-full bg-muted text-xs font-semibold text-foreground uppercase">
                      {getInitials(assignment.staff?.nombre_completo)}
                    </div>
                    <div className="flex flex-col leading-tight min-w-0">
                      <div className="flex items-center gap-2 min-w-0">
                        {/* El nombre de una persona va al mismo tamano que el
                            encabezado "Staff": son del mismo nivel. Estaba a
                            text-xs, el tamano de los datos de apoyo. */}
                        <span className="text-sm font-semibold text-foreground truncate max-w-[120px] sm:max-w-[160px]">
                          {assignment.staff?.nombre_completo || 'Staff sin nombre'}
                        </span>
                        <Badge
                          variant={getRoleBadgeVariant(
                            assignment.rol ||
                              assignment.tipo_coach ||
                              'Coach'
                          )}
                          size="xs"
                        >
                          {formatCoachRole(
                            assignment.rol ||
                              assignment.tipo_coach ||
                              'Coach'
                          )}
                        </Badge>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

        </Card>

        {/* Tabs con subrayado, no pastillas: para secciones de una pagina de
            detalle es el patron que usan GitHub, Linear, Stripe y Vercel. La
            pastilla se lee como control de vista (lista/cuadricula), no como
            navegacion. Antes esto era md:hidden y en desktop las 3 secciones se
            apilaban -- ahora el modelo es el mismo en todos los tamanos. */}
        <Tabs value={activeSection} onValueChange={setActiveSection} className="w-full">
          <TabsList className="scrollbar-none h-auto w-full justify-start gap-6 overflow-x-auto rounded-none border-b bg-transparent p-0 mb-4">
            {SECTION_TABS.map((tab) => (
              <TabsTrigger
                key={tab.id}
                value={tab.id}
                className="shrink-0 rounded-none border-b-2 border-transparent bg-transparent px-0 pb-2.5 pt-2 text-sm font-medium text-muted-foreground shadow-none data-[state=active]:border-foreground data-[state=active]:bg-transparent data-[state=active]:text-foreground data-[state=active]:shadow-none"
              >
                {tab.label}
                <span className="ml-2 rounded-full bg-muted px-1.5 py-0.5 text-xs tabular-nums text-muted-foreground">
                  {tabCounts[tab.id]}
                </span>
              </TabsTrigger>
            ))}
          </TabsList>

        {/* Training Series Section */}
        <TabsContent value="entrenamientos" className="mt-0">
        <Card className="w-full max-w-full">
          {/* Sin titulo ni contador: el tab activo ya dice "Entrenamientos" y trae
              el numero. Sin chevron de colapsar: el tab ya controla la visibilidad,
              dos mecanismos para lo mismo. Queda solo la barra de acciones. */}
          <CardHeader className="pb-3">
            <div className="flex items-center justify-end gap-2">
              {/* Era bg-green-600 hardcodeado: el primario del sistema es el token
                  de marca, no un verde inventado para esta pantalla. */}
              <Button
                onClick={handleStartTraining}
                className="text-xs px-3"
                size="sm"
                title="Iniciar entrenamiento"
              >
                <Play strokeWidth={1.5} className="mr-1 h-3.5 w-3.5" />
                Entrenar
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleGoToReport}
                className="text-xs px-3"
              >
                <Calendar className="h-3.5 w-3.5 mr-1" strokeWidth={1.5} />
                Ver reporte
              </Button>
            </div>
          </CardHeader>

          {(
            activeSeries.length === 0 ? (
              <div className="flex items-center justify-center py-12">
                <div className="text-center px-6">
                  <Clock className="w-12 h-12 text-muted-foreground opacity-30 mx-auto mb-3" />
                  <h3 className="text-foreground font-semibold text-lg mb-1">No hay entrenamientos</h3>
                  <p className="text-muted-foreground text-sm mb-4">
                    {seriesEntrenamiento.length === 0
                      ? 'Este grupo no tiene horarios de entrenamiento. Puedes tomar asistencia igualmente; crea un horario para que la agenda y las horas se llenen solas.'
                      : 'Todos los horarios asignados están inactivos'}
                  </p>
                  {canManageThisGroup && (
                    <Button
                      onClick={() => navigate('/admin/entrenamientos-regulares')}
                      variant="outline"
                      size="sm"
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Crear horario
                    </Button>
                  )}
                </div>
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {activeSeries.map((serie) => {
                  const attendance = getSerieAttendanceSummary(serie.id);
                  return (
                    <div
                      key={serie.id}
                      className="px-6 py-4 hover:bg-muted/50 transition-colors cursor-pointer"
                      onClick={() => {
                        navigate(`/attendance/${group.id}`, {
                          state: {
                            group: group,
                            serieId: serie.id,
                            mode: 'entrenamiento',
                            fecha: getMexicoDateString()
                          }
                        });
                      }}
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <h4 className="text-foreground font-medium mb-2 truncate">{serie.nombre}</h4>
                          <div className="space-y-1 text-sm text-muted-foreground">
                            <div className="flex items-center gap-2">
                              <Clock size={14} />
                              <span>{serie.hora_inicio?.slice(0, 5)} - {serie.hora_fin?.slice(0, 5)}</span>
                            </div>
                            {serie.ubicacion && (
                              <div className="flex items-center gap-2">
                                <MapPin size={14} />
                                <span>{serie.ubicacion}</span>
                              </div>
                            )}
                            {serie.dias_semana && serie.dias_semana.length > 0 && (
                              <div className="flex flex-wrap gap-1 mt-2">
                                {serie.dias_semana.map((dia) => (
                                  <Badge key={dia} variant="outline" className="text-xs">
                                    {dia.charAt(0).toUpperCase() + dia.slice(1)}
                                  </Badge>
                                ))}
                              </div>
                            )}
                            {serie.temporadas && (
                              <div className="mt-2">
                                <Badge variant="secondary" className="text-xs">
                                  {serie.temporadas.nombre}
                                </Badge>
                              </div>
                            )}
                          </div>
                          {attendance ? (
                            <div className="flex flex-wrap gap-3 mt-3 text-xs text-muted-foreground">
                              <span className="flex items-center gap-1 text-green-700 font-semibold">
                                Asistencias
                                <span>{attendance.asistencia}</span>
                              </span>
                              <span className="flex items-center gap-1 text-yellow-700 font-semibold">
                                Retardos
                                <span>{attendance.retardos}</span>
                              </span>
                              <span className="flex items-center gap-1 text-red-700 font-semibold">
                                Faltas
                                <span>{attendance.ausencias}</span>
                              </span>
                              <span className="text-xs text-muted-foreground">
                                Promedio sobre {attendance.finished} entrenamientos terminados
                              </span>
                            </div>
                          ) : (
                            <p className="mt-3 text-xs text-muted-foreground">
                              Aún no hay entrenamientos terminados para calcular asistencias.
                            </p>
                          )}
                        </div>
                        <Badge
                          variant={serie.estatus === 'activo' ? 'default' : 'secondary'}
                          className={serie.estatus === 'activo' ? 'bg-green-600' : ''}
                        >
                          {serie.estatus}
                        </Badge>
                      </div>
                    </div>
                  );
                })}
              </div>
            )
          )}
        </Card>
        </TabsContent>

        {/* Torneos Section */}
        {/* DOS CASAS, no una lista con distintivo. El club vive dos competencias
            distintas: los torneos que el organiza (crea juegos, pone marcadores) y los de
            la red MYKLUBB Torneos (recibe el rol, toma asistencia). Cada seccion se
            presenta sola: titulo completo (en 360px se cortaba en "Torneo..."), una linea
            que dice que es, y su accion en su propio renglon en telefono. La inscripcion
            a la red NO va aqui: es de MYKLUBB Torneos (el dueno, 2026-09-05). */}
        <TabsContent value="torneos" className="mt-0 space-y-4">
        {loadingTorneos ? (
          <Card className="w-full max-w-full">
            <div className="flex items-center justify-center py-10 text-sm text-muted-foreground">
              Cargando torneos asignados...
            </div>
          </Card>
        ) : (
          <>
            <Card data-testid="seccion-internos" className="w-full max-w-full">
              <div className="px-4 pt-4 pb-3 sm:px-6 sm:pt-5">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <Trophy className="h-4 w-4 shrink-0 text-muted-foreground" />
                      <h3 className="font-semibold leading-tight">Torneos internos</h3>
                      <Badge variant="secondary" className="px-1.5 text-[10px]">{torneosInternos.length}</Badge>
                    </div>
                    <p className="mt-1 text-xs text-muted-foreground">
                      Los que organiza tu club. Aquí inscribes tus grupos y capturas los juegos.
                    </p>
                  </div>
                  {/* Por grupo, no por rol: es el grupo de esta persona lo que decide. El
                      RPC inscribir_grupo mira el grupo, asi que aqui se usa la misma regla
                      que ya pasa la base: canManageThisGroup deja pasar al coach y al
                      manager de este grupo, igual que ya pueden tomar asistencia en el. */}
                  {canManageThisGroup && (
                    <Button size="sm" className="w-full shrink-0 sm:w-auto" onClick={() => setShowElegirTorneo(true)}>
                      <Plus className="mr-2 h-4 w-4" /> Inscribir a un torneo interno
                    </Button>
                  )}
                </div>
              </div>
              {torneosInternos.length === 0 ? (
                <div className="border-t px-4 pb-8 pt-5 text-center sm:px-6">
                  <p className="text-sm text-muted-foreground">
                    Este grupo no participa en ningún torneo interno.
                    {canManageThisGroup && <> Con <strong>Inscribir a un torneo interno</strong> lo metes a uno que organice el club.</>}
                  </p>
                </div>
              ) : (
                <div className="border-t">
                  {/* El historico del grupo por temporada: la mas reciente arriba, y
                      "Sin temporada" al final, a la vista, porque es un dato que falta. */}
                  {agruparPorTemporada(torneosInternos).map((bloque) => (
                    <div key={bloque.id}>
                      <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 bg-muted/40 px-4 py-1.5 sm:px-6">
                        <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                          {bloque.nombre}
                        </h4>
                        {bloque.estado === 'cerrada' && (
                          <Badge variant="outline" className="text-[10px]">Cerrada</Badge>
                        )}
                        {bloque.id === SIN_TEMPORADA && (
                          <span className="text-[11px] text-muted-foreground">
                            Edita el torneo y ponle su temporada.
                          </span>
                        )}
                      </div>
                      <div className="divide-y divide-gray-100">
                        {bloque.torneos.map(renderFilaTorneo)}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card>

            <Card data-testid="seccion-red" className="w-full max-w-full">
              <div className="px-4 pt-4 pb-3 sm:px-6 sm:pt-5">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <Globe2 className="h-4 w-4 shrink-0 text-blue-700" />
                      <h3 className="font-semibold leading-tight">MYKLUBB Torneos</h3>
                      <Badge variant="secondary" className="px-1.5 text-[10px]">{torneosRed.length}</Badge>
                    </div>
                    <p className="mt-1 text-xs text-muted-foreground">
                      Los de la red. Aquí solo ves; la inscripción se hace desde MYKLUBB Torneos.
                    </p>
                  </div>
                  <Button size="sm" variant="outline" className="w-full shrink-0 sm:w-auto" onClick={() => navigate('/torneos-myklubb')}>
                    Ir a MYKLUBB Torneos
                  </Button>
                </div>
              </div>
              {torneosRed.length === 0 ? (
                <div className="border-t px-4 pb-8 pt-5 text-center sm:px-6">
                  <p className="text-sm text-muted-foreground">
                    Este grupo no juega en ningún torneo de la red. Se inscribe desde MYKLUBB Torneos, en el torneo al que invitaron al club.
                  </p>
                </div>
              ) : (
                <div className="divide-y divide-gray-100 border-t">
                  {torneosRed.map(renderFilaTorneo)}
                </div>
              )}
            </Card>
          </>
        )}
        </TabsContent>

        {/* Players Section */}
        <TabsContent value="jugadores" className="mt-0">
        <Card className="w-full max-w-full">
          {/* El filtro comparte renglon con las acciones: son la barra de
              herramientas del tab. Antes el filtro colgaba en su propia fila
              debajo del titulo. */}
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2 min-w-0">
                {players.length > 0 && (
                  <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger className="h-8 w-[150px] text-xs">
                      <SelectValue placeholder="Filtrar por estatus" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="todos">Todos los estatus</SelectItem>
                      <SelectItem value="activo">Activo</SelectItem>
                      <SelectItem value="pendiente">Pendiente</SelectItem>
                      <SelectItem value="inactivo">Inactivo</SelectItem>
                    </SelectContent>
                  </Select>
                )}
                {statusFilter !== 'todos' && (
                  <span className="text-xs text-muted-foreground whitespace-nowrap">
                    {filteredGroupPlayers.length} de {players.length}
                  </span>
                )}
                {/* Los problemas de numero se ven sin recorrer la lista, y el aviso ES
                    el atajo a su arreglo. Repetido es un error (rojo); sin numero es un
                    pendiente (ambar), la misma distincion que hace el editor. */}
                {canManageThisGroup && jugadoresConNumeroRepetido > 0 && (
                  <button
                    type="button"
                    onClick={() => setNumerosAbierto(true)}
                    className="flex items-center gap-1 text-xs font-medium text-destructive whitespace-nowrap hover:underline"
                  >
                    <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
                    {jugadoresConNumeroRepetido} números repetidos
                  </button>
                )}
                {canManageThisGroup && jugadoresConNumeroRepetido === 0 && problemasDeNumero.faltantes > 0 && (
                  <button
                    type="button"
                    onClick={() => setNumerosAbierto(true)}
                    className="flex items-center gap-1 text-xs font-medium text-amber-600 whitespace-nowrap hover:underline"
                  >
                    <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
                    {problemasDeNumero.faltantes} sin número
                  </button>
                )}
              </div>
              <div className="flex items-center gap-1.5 shrink-0">
                {canManageThisGroup && (
                  <>
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-8 w-8 p-0 shrink-0"
                      onClick={() => openRosterModal('assign')}
                      aria-label="Asignar jugadores"
                      title="Asignar jugadores"
                    >
                      <UserPlus className="h-3.5 w-3.5" />
                    </Button>
                    {players.length > 0 && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-8 w-8 p-0 shrink-0"
                        onClick={() => openRosterModal('remove')}
                        aria-label="Remover jugadores"
                        title="Remover jugadores"
                      >
                        <UserMinus className="h-3.5 w-3.5" />
                      </Button>
                    )}
                    {players.length > 0 && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-8 w-8 p-0 shrink-0"
                        onClick={() => setNumerosAbierto(true)}
                        aria-label="Números de camiseta"
                        title="Números de camiseta"
                      >
                        <Hash className="h-3.5 w-3.5" />
                      </Button>
                    )}
                  </>
                )}
              </div>
            </div>
          </CardHeader>

          {(
            <>
              {filteredGroupPlayers.length === 0 ? (
                <div className="flex items-center justify-center py-12">
                  <div className="text-center px-6">
                    <Users className="w-12 h-12 text-muted-foreground opacity-30 mx-auto mb-3" />
                    <h3 className="text-foreground font-semibold text-lg mb-1">No hay jugadores</h3>
                    <p className="text-muted-foreground text-sm">
                      Este grupo no tiene jugadores asignados
                    </p>
                  </div>
                </div>
              ) : (
                <div className="divide-y divide-gray-100">
                  {filteredGroupPlayers.map((player) => {
                    const parseMetaArray = (value) => {
                      if (Array.isArray(value)) return value;
                      if (typeof value === 'string') {
                        try {
                          const parsed = JSON.parse(value);
                          return Array.isArray(parsed) ? parsed : [];
                        } catch {
                          return [];
                        }
                      }
                      return [];
                    };
                    const categoryEntries = (player.jugadores_meta || [])
                      .filter((item) => item?.meta_key === 'categorias_por_grupo')
                      .flatMap((item) => parseMetaArray(item?.meta_value));
                    const categoryFromMeta = categoryEntries
                      .filter((entry) => entry?.grupo_id === groupId && entry?.categoria_nombre)
                      .map((entry) => entry.categoria_nombre);
                    const categoryLabelFromMeta = categoryFromMeta.length > 0
                      ? Array.from(new Set(categoryFromMeta)).join(' · ')
                      : null;
                    const normalizedPlayerCategory = (categoryLabelFromMeta || player.categoria_base || '').trim().toLowerCase();
                    const hasCategorySet = groupCategoryNameSet.size > 0;
                    const birthDate = player.fecha_nacimiento ? parseLocalDate(player.fecha_nacimiento) : null;
                    const birthYear = birthDate && !Number.isNaN(birthDate.getTime())
                      ? birthDate.getFullYear()
                      : null;
                    const isOutOfRangeByRules =
                      groupRestrictionType !== 'sin_restriccion' &&
                      groupCategoryRules.length > 0 &&
                      birthDate &&
                      !Number.isNaN(birthDate.getTime())
                        ? !groupCategoryRules.some((rule) => {
                            if (rule.deporte === 'tackle') {
                              if (rule.supDate && birthDate < rule.supDate) return false;
                              if (rule.infDate && birthDate > rule.infDate) return false;
                              return true;
                            }
                            const yearMax = rule.anio_inicio;
                            const yearMin = rule.anio_fin;
                            if (yearMax !== null && birthYear !== null && birthYear > yearMax) return false;
                            if (yearMin !== null && birthYear !== null && birthYear < yearMin) return false;
                            return true;
                          })
                        : false;
                    const isCategoryMismatch =
                      hasCategorySet && normalizedPlayerCategory
                        ? !groupCategoryNameSet.has(normalizedPlayerCategory)
                        : false;
                    const showCategoryAlert =
                      (groupCategoryRules.length > 0 ? isOutOfRangeByRules : isCategoryMismatch) &&
                      groupRestrictionType !== 'sin_restriccion';
                    const categoryAlertVariant =
                      groupRestrictionType === 'estricta' ? 'destructive' : 'warning';
                    const isOutOfRange = showCategoryAlert;
                    const ageBadgeVariant = categoryAlertVariant;
                    const playerCategoryForGroup = normalizedPlayerCategory
                      ? groupCategoryNameSet.size > 0
                        ? (groupCategoryNameSet.has(normalizedPlayerCategory) ? (categoryLabelFromMeta || player.categoria_base) : null)
                        : (categoryLabelFromMeta || player.categoria_base)
                      : null;
                    return (
                      <div
                        key={player.id}
                        className="px-3 sm:px-4 py-3 hover:bg-muted/50 transition-colors w-full"
                      >
                      {/* Mobile Layout */}
                      <div className="sm:hidden w-full">
                        {/* Header with Photo and Name */}
                        <div className="mb-3 relative">
                          {/* Status Badge - Top Right Corner */}
                          <div className="absolute top-0 right-0 z-10">
                            <Badge variant={getStatusVariant(player.group_estatus)} size="xs" className="px-1.5 py-0.5">
                              {player.group_estatus}
                            </Badge>
                          </div>

                          <div className="flex items-start gap-2 pr-16">
                            {/* Photo */}
                            <div className="flex-shrink-0 relative">
                              <div className="relative">
                                <PhotoAvatarButton
                                  imageUrl={player.imagen_url}
                                  name={player.nombre_completo}
                                  size="sm"
                                  onClick={() => canManageThisGroup && foto.abrir(player)}
                                  disabled={foto.subiendoId === player.id}
                                />
                                {foto.subiendoId === player.id && (
                                  <div className="absolute inset-0 flex items-center justify-center">
                                    <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                                  </div>
                                )}
                              </div>
                            </div>

                            {/* Name and Number */}
                            <div className="min-w-0 flex-1">
                          {/* Nombre en su propia linea: compartiendola con los badges
                              se partia en tres renglones. */}
                          <div className="flex items-center gap-2 min-w-0">
                            <NumeroDelGrupo
                              numero={numeroEnGrupo(player.grupo_assignment, player)}
                              repetido={numerosRepetidos.has(numeroEnGrupo(player.grupo_assignment, player))}
                              tam="sm"
                            />
                            <h4 className="text-foreground font-semibold text-sm leading-tight truncate">
                              {player.nombre_completo}
                            </h4>
                          </div>
                          <div className="flex flex-wrap items-center gap-1.5 mt-1 mb-1">
                            {isOutOfRange && (
                              <Badge
                                variant={ageBadgeVariant}
                                size="xs"
                                className="uppercase tracking-wide"
                              >
                                {outOfRangeLabel.charAt(0).toUpperCase() + outOfRangeLabel.slice(1)}
                              </Badge>
                            )}
                            <EstadoParticipacionBadges
                              estado={estadoParticipacion[player.id]?.estado}
                              adeudo={estadoParticipacion[player.id]?.adeudo}
                            />
                          </div>
                              {player.nickname && (
                                <p className="text-xs text-muted-foreground italic">
                                  "{player.nickname}"
                                </p>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Datos del jugador en una linea con separadores, sin prefijos:
                            eran 6 renglones etiquetados que hacian la fila enorme.
                            La CURP NO se muestra en el roster (dato personal: lleva
                            fecha de nacimiento, sexo y entidad); vive en el perfil. */}
                        <p className="text-xs text-muted-foreground">
                          {[
                            player.genero,
                            player.fecha_nacimiento
                              ? parseLocalDate(player.fecha_nacimiento)?.toLocaleDateString('es-MX', {
                                  day: '2-digit', month: 'short', year: 'numeric'
                                })
                              : null,
                            playerCategoryForGroup,
                            player.telefono_jugador
                          ].filter(Boolean).join(' · ')}
                        </p>
                      </div>

                      {/* Desktop Layout */}
                      <div className="hidden sm:flex items-start justify-between w-full max-w-full overflow-hidden">
                        {/* Left Side: Photo and Player Info */}
                        <div className="flex items-center space-x-4 flex-1 min-w-0 max-w-full overflow-hidden">
                          <div className="flex-shrink-0 relative">
                            <div className="relative">
                              <PhotoAvatarButton
                                imageUrl={player.imagen_url}
                                name={player.nombre_completo}
                                size="lg"
                                onClick={() => canManageThisGroup && foto.abrir(player)}
                                disabled={foto.subiendoId === player.id}
                              />
                              {foto.subiendoId === player.id && (
                                <div className="absolute inset-0 flex items-center justify-center">
                                  <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Basic Player Info */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center space-x-2 mb-1">
                              {/* Jersey Number */}
                              <NumeroDelGrupo
                                numero={numeroEnGrupo(player.grupo_assignment, player)}
                                repetido={numerosRepetidos.has(numeroEnGrupo(player.grupo_assignment, player))}
                                tam="lg"
                              />

                              {/* Name */}
                              <h4 className="text-foreground font-semibold text-lg truncate">
                                {player.nombre_completo}
                              </h4>
                              {isOutOfRange && (
                                <Badge
                                  variant={ageBadgeVariant}
                                  className="uppercase tracking-wide text-xs"
                                >
                                  {outOfRangeLabel.charAt(0).toUpperCase() + outOfRangeLabel.slice(1)}
                                </Badge>
                              )}
                              <EstadoParticipacionBadges
                                estado={estadoParticipacion[player.id]?.estado}
                                adeudo={estadoParticipacion[player.id]?.adeudo}
                              />
                            </div>

                            {/* Una sola linea de datos con separadores, sin prefijos ni
                                "|" a mano. La CURP se retiro del roster: es dato
                                personal y su lugar es el perfil del jugador. */}
                            <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-muted-foreground">
                              {player.nickname && (
                                <span className="italic">"{player.nickname}"</span>
                              )}
                              <span>
                                {[
                                  player.genero,
                                  player.fecha_nacimiento
                                    ? parseLocalDate(player.fecha_nacimiento)?.toLocaleDateString('es-MX', {
                                        day: '2-digit', month: 'short', year: 'numeric'
                                      })
                                    : null,
                                  player.telefono_jugador
                                ].filter(Boolean).join(' · ')}
                              </span>
                              {playerCategoryForGroup ? (
                                <Badge variant="secondary" className="text-xs px-2 py-0.5 font-normal">
                                  {playerCategoryForGroup}
                                </Badge>
                              ) : (
                                <Badge variant="outline" className="text-xs px-2 py-0.5 font-normal">
                                  Sin categoría
                                </Badge>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Right Side: Status Badge */}
                        <div className="flex-shrink-0 flex flex-col items-end space-y-2">
                          {/* Player Status - Top Right */}
                          <Badge variant={getStatusVariant(player.group_estatus)} size="sm">
                            {player.group_estatus}
                          </Badge>

                          
                        </div>
                      </div>

                      {/* Legacy Desktop Actions - Keep for compatibility */}
                      <div className="hidden">
                        <div className="flex items-center space-x-3">
                          {/* Action Icons - Desktop */}
                          {player.imagen_url && (
                            <div className="hidden sm:flex items-center space-x-1">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleCopyImageUrl(player.imagen_url);
                                }}
                                className="text-muted-foreground hover:text-foreground transition-colors p-1"
                                title="Copiar URL de imagen"
                              >
                                <Copy size={16} />
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDownloadImage(player.imagen_url, player.nombre_completo);
                                }}
                                className="text-muted-foreground hover:text-foreground transition-colors p-1"
                                title="Descargar foto"
                              >
                                <Download size={16} />
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </Card>
        </TabsContent>
        </Tabs>
      </div>





















































































































































































































      <GroupEditorModal
        open={showEditGroup}
        group={group}
        categories={categorias}
        onClose={() => setShowEditGroup(false)}
        onSaved={async () => {
          await loadGroupData();
          setShowEditGroup(false);
        }}
        showError={(t, m) => alert(t, m)}
        showSuccess={(t, m) => alert(t, m)}
      />

      <Dialog
        open={showAssignAdminStaff}
        onOpenChange={assignStaffGuard.onOpenChange}
      >
        <DialogContent {...assignStaffGuard.dirtyProps} className="max-w-md">
          <DialogHeader>
            <DialogTitle>Asignar Staff</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Selecciona al miembro del staff y el rol que tendrá en este grupo.
            </p>
            {coaches.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                No hay staff disponible para asignar. Revisa la sección de Staff para dar de alta entrenadores.
              </p>
            ) : (
              <div>
                <Label className="block text-sm font-medium mb-1">
                  Miembro del staff
                </Label>
                <Select value={selectedAdminStaffId} onValueChange={(v) => { assignStaffGuard.markDirty(); setSelectedAdminStaffId(v); }}>
                  <SelectTrigger>
                    <SelectValue placeholder="Selecciona un staff" />
                  </SelectTrigger>
                  <SelectContent>
                    {coaches.map((coachItem) => {
                      const roleLabel = formatCoachRole(coachItem.rol || 'coach');
                      return (
                        <SelectItem key={coachItem.id} value={coachItem.id}>
                          <div className="flex items-center justify-between gap-3">
                            <span className="truncate">
                              {coachItem.nombre_completo || coachItem.email}
                            </span>
                            <Badge
                              variant={getRoleBadgeVariant(coachItem.rol || 'coach')}
                              size="xs"
                            >
                              {roleLabel}
                            </Badge>
                          </div>
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>

                <div className="mt-4">
                  <Label className="block text-sm font-medium mb-1">
                    Rol en el grupo
                  </Label>
                  <Select value={selectedAdminStaffRole} onValueChange={(v) => { assignStaffGuard.markDirty(); setSelectedAdminStaffRole(v); }}>
                    <SelectTrigger>
                      <SelectValue placeholder="Selecciona un rol" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Head Coach">Head Coach</SelectItem>
                      <SelectItem value="Asistente">Asistente</SelectItem>
                      <SelectItem value="Manager">Manager</SelectItem>
                      <SelectItem value="Coach">Coach</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="mt-4 flex flex-col gap-3 text-sm text-muted-foreground">
                  <Label className="flex items-center gap-2 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      className="sr-only peer"
                      checked={selectedAdminTarjeton}
                      onChange={(event) => setSelectedAdminTarjeton(event.target.checked)}
                    />
                    <div className="w-9 h-5 bg-muted rounded-full peer peer-checked:bg-emerald-600 relative after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-background after:h-4 after:w-4 after:rounded-full after:transition-all peer-checked:after:translate-x-4" />
                    <span className="text-xs">Aparece en tarjeton</span>
                  </Label>
                </div>
              </div>
            )}
          </div>
          <DialogFooter className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => assignStaffGuard.onOpenChange(false)}
              className="flex-1"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleAssignAdminStaff}
              disabled={
                assigningAdminStaff ||
                !selectedAdminStaffId ||
                coaches.length === 0
              }
              className="flex-1"
            >
              {assigningAdminStaff ? 'Asignando...' : 'Asignar'}
            </Button>
          </DialogFooter>
          {assignStaffGuard.DiscardDialog}
        </DialogContent>
      </Dialog>

      <Dialog
        open={showRemoveStaff}
        onOpenChange={removeStaffGuard.onOpenChange}
      >
        <DialogContent {...removeStaffGuard.dirtyProps} className="max-w-md">
          <DialogHeader>
            <DialogTitle>Remover staff</DialogTitle>
          </DialogHeader>
          {activeStaffAssignments.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No hay staff asignado para remover.
            </p>
          ) : (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Selecciona al staff que deseas remover de este grupo. Su relación se marcará como inactiva.
              </p>
              <div>
                <Label className="block text-sm font-medium mb-1">
                  Staff asignado
                </Label>
                <Select
                  value={selectedStaffToRemove}
                  onValueChange={(v) => { removeStaffGuard.markDirty(); setSelectedStaffToRemove(v); }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecciona staff" />
                  </SelectTrigger>
                  <SelectContent>
                    {activeStaffAssignments.map((assignment) => {
                      const roleLabel = formatCoachRole(
                        assignment.rol ||
                        assignment.tipo_coach ||
                        'Coach'
                      );
                      return (
                        <SelectItem
                          key={assignment.staff_id}
                          value={assignment.staff_id}
                        >
                          <div className="flex items-center justify-between gap-3">
                            <span className="truncate">
                              {assignment.staff?.nombre_completo || 'Staff sin nombre'}
                            </span>
                            <Badge
                              variant={getRoleBadgeVariant(
                                assignment.rol ||
                                assignment.tipo_coach ||
                                'Coach'
                              )}
                              size="xs"
                            >
                              {roleLabel}
                            </Badge>
                          </div>
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
          <DialogFooter className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => removeStaffGuard.onOpenChange(false)}
              className="flex-1"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleRemoveStaff}
              disabled={
                removingStaffMember ||
                !selectedStaffToRemove ||
                activeStaffAssignments.length === 0
              }
              className="flex-1"
            >
              {removingStaffMember ? 'Removiendo...' : 'Remover'}
            </Button>
          </DialogFooter>
          {removeStaffGuard.DiscardDialog}
        </DialogContent>
      </Dialog>

        <GroupRosterModal
          group={group}
          isOpen={showRosterModal}
          initialTab={rosterModalTab}
          onClose={() => setShowRosterModal(false)}
          onSuccess={loadGroupData}
        />

        <ConfirmDialog />

      {foto.PhotoDialog}

      <Dialog open={numerosAbierto} onOpenChange={setNumerosAbierto}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Números de camiseta</DialogTitle>
            <DialogDescription>
              El número que lleva cada jugador en este grupo. Si lo dejas vacío se usa el
              número que tiene en el club.
            </DialogDescription>
          </DialogHeader>
          {numerosAbierto && (
            <NumerosGrupoEditor
              grupoId={group?.id || groupId}
              onEstadoChange={setNumerosEstado}
              saveRef={guardarNumerosRef}
            />
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setNumerosAbierto(false)} disabled={guardandoNumeros}>
              Cancelar
            </Button>
            <Button onClick={guardarNumeros} disabled={guardandoNumeros || numerosEstado.hayRepetidos}>
              {guardandoNumeros ? 'Guardando...' : 'Guardar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {showElegirTorneo && (
        <ElegirTorneoModal
          excluirTorneoIds={groupTorneos.map((t) => t.id)}
          onSelect={(t) => { setTorneoParaInscribir(t); setShowElegirTorneo(false); }}
          onClose={() => setShowElegirTorneo(false)}
        />
      )}

      {torneoParaInscribir && (
        <InscribirGrupoModal
          torneo={torneoParaInscribir}
          grupoFijo={group?.id || groupId}
          grupoFijoNombre={group?.nombre_del_grupo || ''}
          onClose={() => setTorneoParaInscribir(null)}
          onSuccess={() => { setTorneoParaInscribir(null); loadGroupTorneos(); }}
        />
      )}
    </DetailLayout>
  );
};

export default GroupDetails;









































